import { useState, type ReactNode } from 'react';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/components/utils/utils';

export interface FacetComboboxOption {
    value: string;
    label: string;
    /** Riga secondaria sotto l'etichetta: serve a distinguere gli omonimi. */
    hint?: string | null;
    /** Simbolo a sinistra dell'etichetta, nel trigger e nella lista (es. tipo attivita'). */
    icon?: ReactNode;
}

interface FacetComboboxProps {
    options: FacetComboboxOption[];
    value: string | null;
    onChange: (value: string | null) => void;
    /** Testo del pulsante quando non c'e' selezione. */
    placeholder: string;
    searchPlaceholder: string;
    clearLabel: string;
    emptyLabel: string;
    /** Come si cerca quando la lista e' completa: `undefined` = filtro in memoria. */
    query?: string;
    onQueryChange?: (query: string) => void;
    /**
     * La lista non e' tutto. Cambia il significato di ogni stato della tendina, quindi va
     * mostrato sempre e non solo quando non ci sono risultati.
     */
    truncated?: boolean;
    truncatedLabel?: string;
    /** Mostrato al posto dei risultati mentre la ricerca lato server e' in volo. */
    isSearching?: boolean;
    searchingLabel?: string;
    className?: string;
}

/**
 * Tendina di ricerca per una facet dei filtri.
 *
 * Due regimi, decisi da `truncated`:
 * - lista completa: filtra in memoria mentre scrivi, nessuna chiamata;
 * - lista parziale: il chiamante gira la ricerca al server e passa `query`/`onQueryChange`.
 *
 * La differenza che conta e' negli stati. Con la lista parziale un elenco senza risultati
 * non significa "non esiste": puo' voler dire "non l'ho ancora chiesto". Per questo qui
 * `isSearching` ha la precedenza su `CommandEmpty` — mostrare "nessun risultato" mentre la
 * risposta e' in volo insegna a rinunciare proprio quando basterebbe aspettare 200ms.
 */
export const FacetCombobox = ({
    options,
    value,
    onChange,
    placeholder,
    searchPlaceholder,
    clearLabel,
    emptyLabel,
    query,
    onQueryChange,
    truncated,
    truncatedLabel,
    isSearching,
    searchingLabel,
    className,
}: FacetComboboxProps) => {
    const [open, setOpen] = useState(false);
    const selected = options.find((option) => option.value === value);

    // Con la ricerca lato server il filtro l'ha gia' fatto il database: rifarlo qui
    // scarterebbe risultati validi perche' cmdk cerca solo dentro l'etichetta.
    const serverSide = onQueryChange !== undefined;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {/* La X e' un pulsante a se', fuori dal trigger e non un'icona dentro il
                pulsante: annidata, il click sarebbe finito comunque al trigger e avrebbe
                aperto la tendina che stava svuotando. */}
            <div className={cn('relative', className)}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            'w-full justify-between gap-2 font-normal',
                            selected && 'pr-8'
                        )}
                    >
                        <span
                            className={cn(
                                'flex min-w-0 items-center gap-2',
                                !selected && 'text-gray-500'
                            )}
                        >
                            {selected?.icon}
                            <span className="truncate">{selected?.label ?? placeholder}</span>
                        </span>
                        {!selected && <ChevronDown className="w-4 h-4 shrink-0" />}
                    </Button>
                </PopoverTrigger>

                {selected && (
                    <button
                        type="button"
                        aria-label={clearLabel}
                        title={clearLabel}
                        onClick={() => onChange(null)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            <PopoverContent className="w-64 p-0" align="start">
                <Command shouldFilter={!serverSide}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={serverSide ? query : undefined}
                        onValueChange={serverSide ? onQueryChange : undefined}
                    />

                    {truncated && truncatedLabel && (
                        <p className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                            {truncatedLabel}
                        </p>
                    )}

                    <CommandList>
                        {isSearching ? (
                            <div className="flex items-center gap-2 px-3 py-6 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {searchingLabel}
                            </div>
                        ) : (
                            <>
                                <CommandEmpty>{emptyLabel}</CommandEmpty>
                                <CommandGroup>
                                    {options.map((option) => (
                                        <CommandItem
                                            key={option.value}
                                            value={serverSide ? option.value : option.label}
                                            onSelect={() => {
                                                onChange(
                                                    option.value === value ? null : option.value
                                                );
                                                setOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 w-4 h-4 shrink-0',
                                                    option.value === value
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                            {option.icon && (
                                                <span className="mr-2 shrink-0">
                                                    {option.icon}
                                                </span>
                                            )}
                                            <span className="min-w-0">
                                                <span className="block truncate">
                                                    {option.label}
                                                </span>
                                                {option.hint && (
                                                    <span className="block truncate text-xs text-gray-500">
                                                        {option.hint}
                                                    </span>
                                                )}
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
